import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { HiChevronLeft, HiChevronRight, HiChevronDown } from "react-icons/hi2";
import { useTranslation } from "react-i18next";
import { useWhiteLabel } from "../../../providers/WhiteLabel/WhiteLabelProvider";
import { isGuestMode } from "../../../methods/guestMode";
import { isApp } from "../../../native/platform";
import { computeProgress, useOnboarding } from "../../onboarding/onboarding.hook";
import { isPortfolioRequiredFor } from "../../../utils/portfolio";

import {
  LuLayoutDashboard,
  LuSparkles,
  LuRoute,
  LuMessagesSquare,
  LuSearch,
  LuHeart,
  LuBookmark,
  LuHouse,
  LuBookOpen,
  LuChartColumn,
  LuScale,
  LuFolderOpen,
  LuImage,
  LuFolder,
  LuBadgeHelp,
  LuStore,
  LuBriefcaseBusiness,
  LuBuilding2,
  LuFileStack,
  LuReceiptText,
  LuBellRing,
  LuUsers,
  LuSettings,
  LuUser,
  LuTrendingUp,
  LuLandmark,
  LuMegaphone,
  LuBot,
  LuGraduationCap,
  LuWallet,
  LuCreditCard,
  LuEuro,
  LuRocket,
  LuCompass,
  LuUserSearch,
  LuClipboardList,
  LuLayoutGrid,
} from "react-icons/lu";

const SECTION_ROUTES = {
  learning: ["/blog-detail", "/learning-center"],
  estimation: ["/estimation", "/social-estimation"],
  transactionMgmt: ["/my-properties", "/real-estate-transaction-owner", "/seller-file", "/property/qr-code", "/dossier-visite"],
  purchaseRentMgmt: ["/renter-file", "/buyer-file", "/serach-alert", "/mes-recherches"],
  propertySeeker: ["/followed-properties", "/properties?favourites=true"],
  dreamBigger: ["/revons-plus-grand", "/revons-plus-grand/resultats"],
  marketplace: ["/marketplace", "/marketplace/favorites", "/marketplace/orders"],
  marketplaceServices: ["/marketplace", "/pro/marketplace", "/pro/marketplace/sold-services", "/pro/payouts", "/pro/configure-payments"],
  learningCenterPros: ["/learning-center", "/pro-learning"],
  whiteLabel: ["/agency/dashboard", "/agency/settings", "/agency/leads", "/agency/properties", "/agency/hot-leads", "/agency/collaborators", "/agency/marketplace-sold-services"],
  toolbox: ["/boite-a-outils", "/boite-a-outils/applications", "/boite-a-outils/mes-simulations"],
};

const Html = ({
  ListItemLink,
  tabclass,
  isAllow,
  route,
  isOpen = true,
  onToggle = () => {},
  menus,
  user,
  propertyTotal = 0,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { agencyTheme } = useWhiteLabel();
  const { state: onboardingState, loading: onboardingLoading } = useOnboarding();
  const [expandedSection, setExpandedSection] = useState(null);
  const [collapsedOverride, setCollapsedOverride] = useState(null);
  const [hoverOpen, setHoverOpen] = useState(false);
  const [offerProOpen, setOfferProOpen] = useState(false);
  const [portfolioEnabled, setPortfolioEnabled] = useState(false);
  useEffect(() => {
    let alive = true;
    if (user?.accountType === "pro" && user?.proSubFamily) {
      isPortfolioRequiredFor(user.proSubFamily).then((enabled) => {
        if (alive) setPortfolioEnabled(enabled);
      });
    } else {
      setPortfolioEnabled(false);
    }
    return () => { alive = false; };
  }, [user?.accountType, user?.proSubFamily]);
  // Hamburger / app native (mobile) : certains items sont déjà dans la barre de
  // navigation basse → on les masque ici (web mobile < 768px et iOS/Android).
  const [inMobileMenu, setInMobileMenu] = useState(() =>
    typeof window !== "undefined" && (isApp() || window.innerWidth < 768)
  );
  useEffect(() => {
    const onResize = () => setInMobileMenu(isApp() || window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  // Un membre d'agence (agencyOwnerId) accède aux contenus « pro » de l'agence.
  const hasProsContent = user?.accountType === 'pro' || !!user?.agencyOwnerId;
  // Pro de type « Investisseur immobilier ».
  const isInvestor = user?.accountType === 'pro' && String(user?.role || '').toLowerCase() === 'investor';
  // « Mes simulations » n'apparaît que pour un compte connecté (hors invité).
  const isConnected = !!user?.loggedIn && !user?.isGuest && !isGuestMode();
  // Espace application connecté (web, desktop ou mobile) pour un compte pro
  // Agence / Agent : les libellés parlent de « mandats » et « simulations immo ».
  const proRole = String(user?.role || '').toLowerCase();
  const isAgencyOrAgent = hasProsContent && (proRole === 'agency' || proRole === 'agent');
  // Chasseur immobilier : espace dédié (sidebar retravaillée).
  const isHunter = proRole === 'hunter';
  // Pro « Autres » (ex. Diagnostiqueur immobilier) : espace dédié.
  const isProAutres = hasProsContent && proRole === 'other';
  const mandatsMode = isConnected && isAgencyOrAgent;
  // Pro Agence / Agent / Investisseur (espace connecté) : « Les biens » devient
  // « Portefeuille de biens ».
  const portfolioMode =
    isConnected && hasProsContent && ['agency', 'agent', 'investor'].includes(proRole);
  // Parrainage : connecté → tableau de bord (/invite) ; guest → présentation (/parrainage)
  const referralUrl =
    user?.loggedIn && !user?.isGuest && !isGuestMode() ? "/invite" : "/parrainage";
  const learningCenterAccess = hasProsContent && (
    user?.learningCenterEnabled === true ||
    (user?.planId && (user.planId.learningCenterEnabled === true || user.planId.learningCenterEnabled === 'true'))
  );
  const marketplaceAccess = hasProsContent && (
    user?.marketplaceEnabled === true ||
    (user?.planId && (user.planId.marketplaceEnabled === true || user.planId.marketplaceEnabled === 'true'))
  );

  const brandLogo = agencyTheme?.logoUrl || "/assets/img/anyhomes-full-logo.png";
  const onboardingProgress = useMemo(() => {
    if (onboardingLoading) return 0;
    return computeProgress(onboardingState || {});
  }, [onboardingLoading, onboardingState]);

  const isRouteActive = (url) => {
    if (!url || url === "#") return false;

    const [targetPath, targetSearch] = url.split("?");
    if (location.pathname !== targetPath) return false;
    if (!targetSearch) return true;

    const currentParams = new URLSearchParams(location.search);
    const targetParams = new URLSearchParams(targetSearch);

    for (const [key, value] of targetParams.entries()) {
      if (currentParams.get(key) !== value) return false;
    }

    return true;
  };

  const isSectionActive = (sectionId) => {
    return (SECTION_ROUTES[sectionId] || []).some((routeUrl) => isRouteActive(routeUrl));
  };

  const isSectionExpanded = (sectionId) => {
    return (isOpen || hoverOpen) && collapsedOverride !== sectionId && (isSectionActive(sectionId) || expandedSection === sectionId);
  };

  const shouldShowSectionItems = (sectionId) => isSectionExpanded(sectionId);

  const toggleSection = (sectionId) => {
    if (isSectionExpanded(sectionId)) {
      setExpandedSection(null);
      setCollapsedOverride(sectionId);
    } else {
      setExpandedSection(sectionId);
      setCollapsedOverride(null);
    }
  };

  const handleSectionHeaderClick = ({ sectionId, hasSubitems = true, onClick }) => {
    if (!hasSubitems) {
      if (onClick) onClick();
      return;
    }

    if (!isOpen && !hoverOpen) {
      onToggle();
      setExpandedSection(sectionId);
      setCollapsedOverride(null);
      return;
    }

    toggleSection(sectionId);
  };

  useEffect(() => {
    const anyActive = Object.keys(SECTION_ROUTES).some((id) => isSectionActive(id));
    if (!anyActive) setExpandedSection(null);
    setCollapsedOverride(null);
    setHoverOpen(false);
  }, [location.pathname]);

  const MenuItem = ({ icon: Icon, label, url, isCollapsed, dataMenu, matchPaths = [], isBold = false }) => {
    const isPlaceholder = !url || url === "#";
    const isCurrent =
      isRouteActive(url) || matchPaths.some((p) => location.pathname.startsWith(p));

    const content = (
      <>
        <Icon className="menu-icon" size={16} strokeWidth={1.9} />
        <span className="menu-label">{label}</span>
      </>
    );

    if (isPlaceholder) {
      return (
        <span className={`menu-item ${isBold ? "is-bold" : ""}`} {...(dataMenu ? { "data-menu": dataMenu } : {})}>
          {content}
        </span>
      );
    }

    return (
      <NavLink
        to={url}
        end
        className={`menu-item ${isCurrent ? "is-current" : ""} ${isBold ? "is-bold" : ""}`}
        {...(dataMenu ? { "data-menu": dataMenu } : {})}
      >
        {content}
      </NavLink>
    );
  };

  const SectionHeader = ({
    icon: Icon,
    label,
    sectionId,
    isCollapsed,
    hasSubitems = true,
    isBold = false,
    onClick,
  }) => (
    <div
      className={`section-header ${isSectionActive(sectionId) ? "section-current" : ""} ${
        !isSectionActive(sectionId) && isSectionExpanded(sectionId) ? "section-open" : ""
      } ${isBold ? "is-bold" : ""}`}
      onClick={() => handleSectionHeaderClick({ sectionId, hasSubitems, onClick })}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleSectionHeaderClick({ sectionId, hasSubitems, onClick });
        }
      }}
    >
      <div className="section-title-wrapper">
        <Icon className="section-icon" size={16} strokeWidth={1.9} />
        <span className="section-title">{label}</span>
      </div>

      {hasSubitems && (
        <HiChevronDown
          className={`section-chevron ${shouldShowSectionItems(sectionId) ? "expanded" : ""}`}
          size={16}
        />
      )}
    </div>
  );

  const GroupTitle = ({ label }) => (
    <div className="sidebar-group-title">{label}</div>
  );

  return (
    <div
      className={`sidebar-wrapper ${isOpen ? "sidebar-open" : "sidebar-collapsed"}${!isOpen && hoverOpen ? " hover-expanded" : ""}`}
      onMouseMove={(e) => { if (!isOpen && !e.target.closest('.sidebar-block-logo')) setHoverOpen(true); }}
      onMouseLeave={() => setHoverOpen(false)}
    >

      {/* ── Bloc 1 : Logo ── */}
      <div className="sidebar-block sidebar-block-logo">
        <div className="sidebar-top-row">
          {isOpen || hoverOpen ? (
            <span className="sidebar-logo-wrap">
              <NavLink to="/" title={agencyTheme?.agencyName || "AnyHomes"}>
                <img
                  id="sidebar-logo-img"
                  src={brandLogo}
                  alt={agencyTheme?.agencyName || "AnyHomes"}
                  className="sidebar-logo"
                  onError={(e) => { e.target.src = '/assets/img/anyhomes-full-logo.png'; }}
                />
              </NavLink>
              {isConnected && hasProsContent && (
                <img
                  src="/assets/img/anyhomes-pro-badge.png"
                  alt="Pro"
                  className="sidebar-pro-badge"
                />
              )}
            </span>
          ) : (
            <NavLink to="/" title={agencyTheme?.agencyName || "AnyHomes"} className="sidebar-logo-a-wrap">
              <img
                id="sidebar-logo-a-img"
                src={agencyTheme?.logoUrl ? brandLogo : '/assets/img/anyhomes-icon-logo-brand.webp'}
                alt={agencyTheme?.agencyName || "A"}
                className="sidebar-logo sidebar-logo-compact"
                onError={(e) => { e.target.src = agencyTheme?.logoUrl ? '/assets/img/anyhomes-logo-a.png' : '/assets/img/anyhomes-icon-logo-brand.webp'; }}
              />
            </NavLink>
          )}

          <button
            className="toggle-btn always-visible"
            onClick={onToggle}
            type="button"
            title={isOpen ? "Réduire le menu" : "Déployer le menu"}
          >
            {isOpen ? <HiChevronLeft size={16} /> : <HiChevronRight size={16} />}
          </button>
        </div>
      </div>

      {/* Zone défilante : tout le menu sauf le logo (en mobile, seul le logo reste fixe) */}
      <div className="sidebar-scroll">

      {/* ── Découvrir (Parrainage + Offre pro) — mobile uniquement.
           Sur desktop ces actions sont dans le header (header InApp).
           Même règle en web mobile et en app native (source de vérité : web). */}
      <div className="sidebar-block sidebar-block-primary md:hidden">
        <GroupTitle label={t('navigation.groupDiscover', 'Découvrir')} />
        <div className={`sidebar-menu sidebar-menu-discover offer-pro-nav ${offerProOpen ? "open" : ""}`}>
          <MenuItem icon={LuEuro} label={t('header.referral')} url={referralUrl} isCollapsed={!isOpen} dataMenu="referral" />
          {/* Offre pro (offers-pro / offers-pro-investor) : réservé au web.
              Retiré de l'app native (aucune référence à un achat d'abonnement). */}
          {!isApp() && (
            <>
              <button
                type="button"
                className="menu-item"
                onClick={() => setOfferProOpen((v) => !v)}
                data-menu="offerPro"
              >
                <LuBuilding2 className="menu-icon" size={16} strokeWidth={1.9} />
                <span className="menu-label">{t('header.offerPro', 'Offre pro')}</span>
                <HiChevronDown className="menu-chevron" size={14} />
              </button>
              {offerProOpen && (
                <div className="offer-pro-submenu">
                  <NavLink to="/offers-pro-agent" end className="menu-item" onClick={() => setOfferProOpen(false)}>
                    <span className="menu-label" style={{ marginLeft: 22 }}>{t('header.offerProAgent', 'Agents indépendants')}</span>
                  </NavLink>
                  <NavLink to="/offers-pro" end className="menu-item" onClick={() => setOfferProOpen(false)}>
                    <span className="menu-label" style={{ marginLeft: 22 }}>{t('header.offerProAgency', 'Agences immobilières')}</span>
                  </NavLink>
                  <NavLink to="/offers-pro-investor" end className="menu-item" onClick={() => setOfferProOpen(false)}>
                    <span className="menu-label" style={{ marginLeft: 22 }}>{t('header.offerProInvestor', "Investisseurs Immobiliers")}</span>
                  </NavLink>
                  <NavLink to="/offers-pro-hunter" end className="menu-item" onClick={() => setOfferProOpen(false)}>
                    <span className="menu-label" style={{ marginLeft: 22 }}>{t('header.offerProHunter', 'Chasseur immobilier')}</span>
                  </NavLink>
                  <NavLink to="/offers-pro-diagnostiqueur" end className="menu-item" onClick={() => setOfferProOpen(false)}>
                    <span className="menu-label" style={{ marginLeft: 22 }}>{t('header.offerProDiagnostic', 'Diagnostiqueurs immobiliers')}</span>
                  </NavLink>
                  <NavLink to="/offers-pro-decorateur" end className="menu-item" onClick={() => setOfferProOpen(false)}>
                    <span className="menu-label" style={{ marginLeft: 22 }}>{t('header.offerProDecorator', "Décorateurs d'intérieur")}</span>
                  </NavLink>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {isProAutres ? (
        <>
          {/* ── Pro/Autres : Piloter mon activité ── */}
          <div className="sidebar-block sidebar-block-primary">
            <GroupTitle label={t('navigation.groupPilotActivity', 'Piloter mon activité')} />
            <div className="sidebar-menu">
              <MenuItem icon={LuLayoutDashboard} label={t('navigation.dashboard', 'Tableau de bord')} url="/dashboard" isCollapsed={!isOpen} dataMenu="dashboard" />
              <MenuItem icon={LuBot} label={t('navigation.myAgentIA', 'Mon assistant IA')} url="/agent-autonome" isCollapsed={!isOpen} dataMenu="myAgentImmo" />
              <MenuItem icon={LuMessagesSquare} label={t('navigation.messages', 'Messages')} url="/chat" isCollapsed={!isOpen} dataMenu="messages" />
            </div>
          </div>

          {/* ── Pro/Autres : Mon offre de services + outils + espace pro ── */}
          <div className="sidebar-block sidebar-block-sections">
            <GroupTitle label={t('navigation.groupServiceOffer', 'Mon offre de services')} />
            <div className="sidebar-menu">
              <MenuItem icon={LuStore} label={t('navigation.myServices', 'Mes services')} url="/pro/marketplace" isCollapsed={!isOpen} />
              <MenuItem icon={LuReceiptText} label={t('navigation.servicesSold', 'Services vendus')} url="/pro/marketplace/sold-services" isCollapsed={!isOpen} />
              <MenuItem icon={LuWallet} label={t('navigation.payouts', 'Mes versements')} url="/pro/payouts" isCollapsed={!isOpen} />
              <MenuItem icon={LuCreditCard} label={t('navigation.configurePayments', 'Configurer paiements')} url="/pro/configure-payments" isCollapsed={!isOpen} />
            </div>

            <div className="menu-divider" />

            <GroupTitle label={t('navigation.groupWorkTools', 'Mes outils de travail')} />
            <div className="sidebar-menu">
              <MenuItem icon={LuCompass} label={t('navigation.repereImmo', 'Repères immobiliers')} url="/boite-a-outils/applications" isCollapsed={!isOpen} />
              <MenuItem icon={LuClipboardList} label={t('navigation.mySearches', 'Mes recherches')} url="/boite-a-outils/mes-simulations" isCollapsed={!isOpen} />
            </div>

            <div className="menu-divider" />

            {marketplaceAccess && (
              <>
                <GroupTitle label={t('navigation.groupManageClients', 'Gérer mes clients')} />
                <div className="sidebar-menu">
                  <MenuItem icon={LuFolderOpen} label={t('navigation.clientFolders', 'Dossiers clients')} url="/pro/dossiers-clients" isCollapsed={!isOpen} dataMenu="clientFolders" />
                </div>
                <div className="menu-divider" />
              </>
            )}

            <GroupTitle label={t('navigation.groupManagePro', 'Gérer mon espace pro')} />
            <div className="sidebar-menu">
              <div className="menu-section expandable">
                <SectionHeader
                  icon={LuBriefcaseBusiness}
                  label={t('navigation.companyProfile', 'Profil entreprise')}
                  sectionId="companyProfile"
                  isCollapsed={!isOpen}
                  hasSubitems={false}
                  onClick={() => navigate('/profile')}
                />
              </div>
              {portfolioEnabled && (
                <MenuItem icon={LuImage} label={t('navigation.portfolio', 'Portfolio')} url="/pro/portfolio" isCollapsed={!isOpen} />
              )}
              {user?.accountType === 'pro' && (user?.isAgencyOwner || user?.whiteLabelActive) && (
                <MenuItem icon={LuUsers} label={t('navigation.agencyCollaborators', 'Gestion des collaborateurs')} url="/agency/collaborators" isCollapsed={!isOpen} />
              )}
            </div>
          </div>
        </>
      ) : (
        <>
      {/* ── Groupe 1 : Piloter mon projet ── */}
      <div className="sidebar-block sidebar-block-primary">
        <GroupTitle label={isHunter ? t('navigation.groupProjectMandats', 'Piloter mes mandats') : mandatsMode ? t('navigation.groupProjectMandats', 'Piloter mes mandats') : hasProsContent ? t('navigation.groupProjectPro', 'Piloter ma transaction') : t('navigation.groupProject', 'Piloter mon projet')} />
        <div className="sidebar-menu">
          {!onboardingLoading && onboardingState && (isInvestor || (!onboardingState.completionCelebrationSeen && onboardingProgress < 100)) ? (
            <MenuItem icon={LuRocket} label={isHunter ? t('navigation.onboardingHunter', 'Démarrer avec AnyHomes') : t('navigation.onboarding')} url="/onboarding" isCollapsed={!isOpen} dataMenu="onboarding" />
          ) : null}
          <MenuItem icon={LuLayoutDashboard} label={t('navigation.dashboard')} url="/dashboard" isCollapsed={!isOpen} dataMenu="dashboard" />
          {!inMobileMenu && (
            <MenuItem icon={LuRoute} label={isHunter ? t('navigation.transactionToolPro', 'Parcours immo digital') : hasProsContent ? t('navigation.transactionToolPro', 'Parcours immo digital') : t('navigation.transactionTool')} url="/transaction-dashboard" isCollapsed={!isOpen} dataMenu="transactionTool" />
          )}
          <MenuItem icon={LuBot} label={t('navigation.myAgentImmo', 'Mon assistant immo IA')} url="/agent-autonome" isCollapsed={!isOpen} dataMenu="myAgentImmo" />
          {!inMobileMenu && (
            <MenuItem icon={LuMessagesSquare} label={t('navigation.messages')} url="/chat" isCollapsed={!isOpen} dataMenu="messages" />
          )}
        </div>
      </div>

      {/* ── Groupe 2 : Accélérer mon projet immo ── */}
      <div className="sidebar-block sidebar-block-sections">
        <GroupTitle label={isHunter ? t('navigation.groupAdvanceHunter', 'Accélérer mes mandats de recherche') : hasProsContent ? t('navigation.groupAdvancePro', 'Accélérer mes transactions') : t('navigation.groupAdvance', 'Accélérer mon projet immo')} />

        <div className="sidebar-menu">
          {!inMobileMenu && (
            <MenuItem icon={LuSearch} label={t('navigation.searchProperties')} url="/properties" isCollapsed={!isOpen} dataMenu="searchProperties" isBold />
          )}
          {!hasProsContent && !inMobileMenu && (
            <MenuItem icon={LuStore} label={t('navigation.proImmobilierALaCarte', 'Services immo à la carte')} url="/marketplace" isCollapsed={!isOpen} dataMenu="marketplaceServices" />
          )}
          <MenuItem icon={LuUserSearch} label={t('navigation.searchListingsDiscover', 'Ils cherchent un bien')} url="/recherches" isCollapsed={!isOpen} dataMenu="searchListingsDiscover" />
          {!hasProsContent && !isInvestor && !isHunter && (
            <MenuItem icon={LuSparkles} label={t('navigation.dreamBigger', 'Rêvons plus grand')} url="/revons-plus-grand" isCollapsed={!isOpen} dataMenu="dreamBigger" />
          )}
          <MenuItem icon={LuHeart} label={isHunter ? t('navigation.followedPropertiesHunter', 'Ma sélection de biens') : hasProsContent ? t('navigation.followedPropertiesPro', 'Biens à surveiller') : t('navigation.followedProperties')} url="/followed-properties" isCollapsed={!isOpen} dataMenu="followedProperties" />

          {isHunter ? (
            /* Chasseur : « Baromètre prix » devient un item simple (non dépliable)
               qui ouvre la page /estimation. */
            <MenuItem icon={LuScale} label={t('navigation.p2pEstimation', 'Baromètre prix')} url="/estimation" isCollapsed={!isOpen} dataMenu="estimation" />
          ) : (
          <div className="menu-section expandable" data-menu="estimation">
            <SectionHeader icon={LuScale} label={t('navigation.p2pEstimation')} sectionId="estimation" isCollapsed={!isOpen} />
            {shouldShowSectionItems("estimation") && (
              <div className="section-items">
                <MenuItem icon={LuChartColumn} label={t('navigation.estimateProperties')} url="/estimation" isCollapsed={!isOpen} />
                <MenuItem icon={LuUsers} label={t('navigation.campaignManager')} url="/social-estimation" isCollapsed={!isOpen} />
              </div>
            )}
          </div>
          )}

          {!isHunter && (
          <div className="menu-section expandable" data-menu="transactionMgmt">
            <SectionHeader icon={LuFileStack} label={mandatsMode ? t('navigation.ownerToolsMandats', 'Outils pour mes mandats') : hasProsContent ? t('navigation.ownerToolsPro', 'Outils agences') : t('navigation.ownerTools')} sectionId="transactionMgmt" isCollapsed={!isOpen} isBold />
            {shouldShowSectionItems("transactionMgmt") && (
              <div className="section-items">
                <MenuItem icon={LuHouse} label={portfolioMode ? t('navigation.myPropertiesPortfolio', 'Portefeuille de biens') : hasProsContent ? t('navigation.myPropertiesPro', 'Les biens') : t('navigation.myProperties')} url="/my-properties" isCollapsed={!isOpen} dataMenu="my-properties" />
                <MenuItem icon={LuFolderOpen} label={t('navigation.sellerFile')} url="/seller-file" isCollapsed={!isOpen} />
                <MenuItem icon={LuBadgeHelp} label={t('navigation.qrCode')} url="/property/qr-code" isCollapsed={!isOpen} />
                <MenuItem icon={LuFolder} label={t('navigation.dossierVisite')} url="/dossier-visite" isCollapsed={!isOpen} />
                {hasProsContent && (
                  <MenuItem icon={LuBellRing} label={t('navigation.searchAlerts')} url="/serach-alert" isCollapsed={!isOpen} />
                )}
              </div>
            )}
          </div>
          )}

          {!hasProsContent && (
            <div className="menu-section expandable" data-menu="purchaseRentMgmt">
              <SectionHeader icon={LuBriefcaseBusiness} label={t('navigation.candidateTools')} sectionId="purchaseRentMgmt" isCollapsed={!isOpen} isBold />
              {shouldShowSectionItems("purchaseRentMgmt") && (
                <div className="section-items">
                  <MenuItem icon={LuFolderOpen} label={t('navigation.renterFile')} url="/renter-file" isCollapsed={!isOpen} />
                  <MenuItem icon={LuFolderOpen} label={t('navigation.buyerFile')} url="/buyer-file" isCollapsed={!isOpen} />
                  <MenuItem icon={LuBellRing} label={t('navigation.searchAlerts')} url="/serach-alert" isCollapsed={!isOpen} />
                  <MenuItem icon={LuClipboardList} label={t('navigation.mySearchListings', 'Mes annonces de recherche')} url="/mes-recherches" isCollapsed={!isOpen} />
                </div>
              )}
            </div>
          )}

          <div className="menu-section expandable" data-menu="toolbox">
            <SectionHeader icon={LuCompass} label={mandatsMode ? t('navigation.toolboxMandats', 'Simulations immo') : isInvestor ? t('navigation.investorTools', 'Outils investisseurs') : t('navigation.toolbox', 'Mes repères immo')} sectionId="toolbox" isCollapsed={!isOpen} />
            {shouldShowSectionItems("toolbox") && (
              <div className="section-items">
                <MenuItem icon={LuHouse} label={t('navigation.analyzeProperty', 'Analyser un bien')} url="/boite-a-outils" isCollapsed={!isOpen} />
                <MenuItem icon={LuLayoutGrid} label={t('repere.menu.apps', 'Les applications')} url="/boite-a-outils/applications" isCollapsed={!isOpen} />
                {isConnected && (
                  <MenuItem icon={LuClipboardList} label={t('repere.menu.sims', 'Mes simulations')} url="/boite-a-outils/mes-simulations" isCollapsed={!isOpen} />
                )}
              </div>
            )}
          </div>
        </div>

        <div className="menu-divider" />

        {/* ── Groupe 3 : Me faire accompagner ── */}
        <GroupTitle label={isHunter ? t('navigation.groupAccompanyHunter', 'Services et contenus') : isAgencyOrAgent && marketplaceAccess ? t('navigation.groupManageClients', 'Gérer mes clients') : hasProsContent ? t('navigation.groupAccompanyPro', 'Contenus et services') : t('navigation.groupAccompany', 'Me faire accompagner')} />

        <div className="sidebar-menu">
          {isAgencyOrAgent ? (
            marketplaceAccess ? (
              <MenuItem icon={LuFolderOpen} label={t('navigation.clientFolders', 'Dossiers clients')} url="/pro/dossiers-clients" isCollapsed={!isOpen} dataMenu="clientFolders" />
            ) : null
          ) : (
            <>
              <MenuItem icon={LuBookOpen} label={t('navigation.learningCenter')} url="/learning-center" isCollapsed={!isOpen} dataMenu="learningCenter" />

              {(!hasProsContent || marketplaceAccess) && (
                <div className="menu-section expandable" data-menu="marketplace">
                  <SectionHeader icon={LuStore} label={t('navigation.proImmobilierALaCarte')} sectionId="marketplace" isCollapsed={!isOpen} />
                  {shouldShowSectionItems("marketplace") && (
                    <div className="section-items">
                      <MenuItem icon={LuStore} label={t('navigation.marketplaceList')} url="/marketplace" isCollapsed={!isOpen} />
                      <MenuItem icon={LuBookmark} label={t('navigation.marketplaceFavorites')} url="/marketplace/favorites" isCollapsed={!isOpen} />
                      <MenuItem icon={LuReceiptText} label={t('navigation.marketplaceOrders')} url="/marketplace/orders" isCollapsed={!isOpen} />
                      {!hasProsContent && (
                        <MenuItem icon={LuFolderOpen} label={t('navigation.documentSharing', 'Partage de documents')} url="/partage-documents" isCollapsed={!isOpen} dataMenu="documentSharing" />
                      )}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {user?.whiteLabelAgencyId && (
            <MenuItem icon={LuLandmark} label={t('navigation.myAgency', 'Mon agence immobilière')} url="/my-agency" matchPaths={['/company-details']} isCollapsed={!isOpen} />
          )}

          {!hasProsContent && (
            <MenuItem icon={LuBuilding2} label={t('navigation.findAgency', 'Trouver la bonne agence')} url="/prolist" isCollapsed={!isOpen} dataMenu="findAgency" />
          )}
        </div>

        {hasProsContent && !isAgencyOrAgent && marketplaceAccess && (
          <>
            <div className="menu-divider" />
            <GroupTitle label={t('navigation.groupManageClients', 'Gérer mes clients')} />
            <div className="sidebar-menu">
              <MenuItem icon={LuFolderOpen} label={t('navigation.clientFolders', 'Dossiers clients')} url="/pro/dossiers-clients" isCollapsed={!isOpen} dataMenu="clientFolders" />
            </div>
          </>
        )}

        {hasProsContent && <div className="menu-divider" />}

        {/* ── Groupe 4 : AnyHomes pour les pros ── */}
        {hasProsContent && <GroupTitle label={mandatsMode ? t('navigation.groupProsMandats', 'Gérer mon espace pro') : t('navigation.groupPros', 'AnyHomes pour les pros')} />}

        <div className="sidebar-menu">
          {(user?.accountType === 'pro' || !!user?.agencyOwnerId) && (
            <div className="menu-section expandable">
              <SectionHeader
                icon={LuBriefcaseBusiness}
                label={t('navigation.companyProfile')}
                sectionId="companyProfile"
                isCollapsed={!isOpen}
                hasSubitems={false}
                onClick={() => navigate('/profile')}
              />
            </div>
          )}

          {user?.accountType === 'pro' && (user?.isAgencyOwner || user?.whiteLabelActive) && (
            <div className="menu-section" data-menu="agencyTeam">
              <MenuItem icon={LuUsers} label={t('navigation.agencyCollaborators')} url="/agency/collaborators" isCollapsed={!isOpen} />
            </div>
          )}

          {learningCenterAccess && (
            <div className="menu-section expandable" data-menu="learningCenterPros">
              <SectionHeader icon={LuGraduationCap} label={t('navigation.learningCenterPros', 'Learning Center')} sectionId="learningCenterPros" isCollapsed={!isOpen} />
              {shouldShowSectionItems("learningCenterPros") && (
                <div className="section-items">
                  {isAgencyOrAgent && (
                    <MenuItem icon={LuBookOpen} label={t('navigation.allContents', 'Tous les contenus')} url="/learning-center" isCollapsed={!isOpen} />
                  )}
                  <MenuItem icon={LuBookOpen} label={t('navigation.myContent', 'Mes contenus')} url="/pro-learning" isCollapsed={!isOpen} />
                </div>
              )}
            </div>
          )}

          {marketplaceAccess && (
            <div className="menu-section expandable" data-menu="marketplaceServices">
              <SectionHeader icon={LuStore} label={t('navigation.marketplaceServices')} sectionId="marketplaceServices" isCollapsed={!isOpen} />
              {shouldShowSectionItems("marketplaceServices") && (
                <div className="section-items">
                  {isAgencyOrAgent && (
                    <MenuItem icon={LuStore} label={t('navigation.viewAllServices', 'Voir tous les services')} url="/marketplace" isCollapsed={!isOpen} />
                  )}
                  <MenuItem icon={LuLayoutDashboard} label={t('navigation.proDashboard')} url="/pro/marketplace" isCollapsed={!isOpen} />
                  <MenuItem icon={LuReceiptText} label={t('navigation.servicesSold')} url="/pro/marketplace/sold-services" isCollapsed={!isOpen} />
                  <MenuItem icon={LuWallet} label={t('navigation.payouts', 'Mes versements')} url="/pro/payouts" isCollapsed={!isOpen} />
                  {/* Réservé au PROPRIÉTAIRE du compte (les collaborateurs membres ne la voient pas). */}
                  {user?.accountType === 'pro' && !user?.agencyOwnerId && (
                    <MenuItem icon={LuCreditCard} label={t('navigation.configurePayments', 'Configurer paiements')} url="/pro/configure-payments" isCollapsed={!isOpen} />
                  )}
                </div>
              )}
            </div>
          )}

          {user?.accountType === 'pro' && user?.whiteLabelActive && (
            <div className="menu-section expandable" data-menu="whiteLabel">
              <SectionHeader icon={LuBuilding2} label={t('navigation.whiteLabel')} sectionId="whiteLabel" isCollapsed={!isOpen} />
              {shouldShowSectionItems("whiteLabel") && (
                <div className="section-items">
                  <MenuItem icon={LuLayoutDashboard} label={t('navigation.agencyDashboard')} url="/agency/dashboard" isCollapsed={!isOpen} />
                  <MenuItem icon={LuUser} label={t('navigation.agencyLeads')} url="/agency/leads" isCollapsed={!isOpen} />
                  <MenuItem icon={LuHouse} label={t('navigation.agencyProperties')} url="/agency/properties" isCollapsed={!isOpen} />
                  <MenuItem icon={LuTrendingUp} label={t('navigation.agencyHotLeads')} url="/agency/hot-leads" isCollapsed={!isOpen} />
                  <MenuItem icon={LuStore} label={t('navigation.agencyMarketplaceDedicated')} url="/agency/marketplace-sold-services" isCollapsed={!isOpen} />
                  <MenuItem icon={LuSettings} label={t('navigation.configuration')} url="/agency/settings" isCollapsed={!isOpen} />
                </div>
              )}
            </div>
          )}

          {user?.isAdmin && (
            <div className="menu-section expandable">
              <SectionHeader
                icon={LuMegaphone}
                label={t('navigation.referralAdmin')}
                sectionId="adminReferral"
                isCollapsed={!isOpen}
                hasSubitems={false}
                onClick={() => navigate('/admin/referrals')}
              />
            </div>
          )}
        </div>
      </div>
        </>
      )}
      </div>
    </div>
  );
};

export default React.memo(Html);
